import { encounterZoneLabel } from '../janus-engine/compile/encounter-zone-label';
import { displayEncounterRoomLabel, type EncounterDisplayInput } from '../janus-engine/port/k5-render/encounter-display';
// push-encounter-creature-form.ts — the GM "Push to Encounter" form (owner 2026-09-11), opened from the
// FT3D creature wheel into the sidelog Forms tab like the Edit Creature form. Pick a destination
// encounter in this campaign, then a room, then who goes — all three searchable Tom Selects, the roster
// with portraits. The shared browser planner places compounds and living summons; its truth commit
// moves them in one transaction and relays both encounters.

interface PushEncounter { id: string; name: string; zone_parts: { encounters: Record<string, unknown> }; location: string; private: boolean; explored: boolean }
interface PushRosterEntry { id: string; name: string; pc: boolean; thumb: string | null; dead: boolean; summonOf: string }
interface PushRoom { id: string; name: string; floor: number; mapKey?: string }
interface PushResult {
    ok: boolean;
    error?: string;
    message?: string;
    correlationId?: string;
    sourceEncounterId?: string;
    destinationEncounterId?: string;
    encounters?: PushEncounter[];
    roster?: PushRosterEntry[];
    rooms?: PushRoom[];
    roomZoneParts?: EncounterDisplayInput;
    movedCount?: number;
    gm_boundary?: unknown;
}

type TomSelectLike = {
    clear(silent?: boolean): void;
    clearOptions(): void;
    addOption(option: { value: string; text: string }): void;
    refreshOptions(open?: boolean): void;
    setValue(value: string | string[], silent?: boolean): void;
    disable(): void;
    enable(): void;
};
type TsSelect = HTMLSelectElement & { tomselect?: TomSelectLike };
type PushHost = Window & {
    ftCampaignCsrfToken?: unknown;
    loader?: string;
    forceOpenSideLog?: () => void;
    initializeTomSelect?: () => void;
    getSidelogContext?: () => { campaign_id?: unknown } | null;
    FateTide?: { campaign?: { id?: unknown } };
};

const PUSH_READY = '';
const PUSH_NO_ROOMS = 'That encounter has no rooms to arrive in.';
const PUSH_ERRORS: Record<string, string> = {
    formation_blocked: 'The encounter is full. There is no free floor for the selected creatures.',
    room_not_found: 'That room no longer exists — pick another.',
    gm_access_required: 'Only the campaign GM can push creatures.',
    csrf_invalid: 'Your session changed — reload the page and try again.',
    creature_moved_during_send: 'Someone moved first — try again.',
    encounter_campaign_mismatch: 'That encounter is not part of this campaign.',
    creature_not_in_campaign: 'This creature is not in an encounter of this campaign.',
    creature_not_found: 'One of those creatures no longer exists.',
    nothing_to_move: 'Pick at least one creature.',
};

const pushHost = (): PushHost => window as PushHost;

/** The site's standard loading spinner (template.js `loader`). */
function pushSpinner(): string {
    return String(pushHost().loader ?? "<div class='d-flex justify-content-center my-2'><div class='spinner-border' role='status'></div></div>");
}

function pushCampaignId(): string {
    const host = pushHost();
    const fromSidelog = String(host.getSidelogContext?.()?.campaign_id ?? '').trim();
    return fromSidelog || String(host.FateTide?.campaign?.id ?? '').trim();
}

async function postPush(body: Record<string, unknown>): Promise<PushResult> {
    const run = (window as Window & { ftGeometryRelocation?: (body: Record<string, unknown>) => Promise<PushResult> }).ftGeometryRelocation;
    if (!run) return { ok: false, error: 'relocation:browser-not-loaded' };
    const result = await run(body);
    if (body.action === 'context' && result.rooms && result.roomZoneParts) {
        for (const room of result.rooms) room.mapKey = displayEncounterRoomLabel(result.roomZoneParts, room.mapKey ?? '', result.rooms.length);
    }
    return result;
}

function pushErrorText(result: PushResult): string {
    const tail = result.correlationId ? ` (ref ${result.correlationId.slice(0, 8)})` : '';
    return `${result.message ?? PUSH_ERRORS[result.error ?? ''] ?? `Push failed: ${result.error ?? 'unknown error'}`}${tail}`;
}

function encounterLabel(encounter: PushEncounter, sourceId: string): string {
    return [String(encounterZoneLabel(encounter.zone_parts)), encounter.name || 'Untitled encounter'].filter(Boolean).join(' · ')
        + (encounter.id === sourceId ? ' (here)' : '') + (encounter.private ? ' · private' : '');
}

function roomLabel(room: PushRoom): string {
    const name = room.mapKey ? `${room.mapKey} · ${room.name}` : room.name;
    return room.floor ? `${name} · floor ${room.floor} ft` : name;
}

/** Tom Select renders a static option's data-icon HTML; a portrait when we have one, else the type icon. */
function rosterIconHtml(entry: PushRosterEntry): string {
    return entry.thumb
        ? `<img src="${escapeHtml(entry.thumb)}" width="25" height="25" alt="" style="object-fit:cover;border-radius:4px">`
        : iconMap(entry.pc ? 'player_character' : 'creature', 25, 25, false);
}

export async function ftOpenPushEncounterCreatureForm(
    ecId: string,
    options: { destination?: string; notice?: string } = {},
): Promise<void> {
    const creatureId = String(ecId || '').trim().toUpperCase();
    const host = pushHost();
    try { host.forceOpenSideLog?.(); } catch { /* optional chrome */ }
    (document.getElementById('pills_sidelog_form_tab') as HTMLElement | null)?.click();
    const container = document.getElementById('div_refresh_sidelog_form');
    if (!container) return;
    container.innerHTML = pushSpinner();

    const campaignId = pushCampaignId();
    if (!campaignId || !host.ftCampaignCsrfToken) {
        container.innerHTML = '<p class="p-2">Push to Encounter works on the campaign play page.</p>';
        return;
    }
    const context = await postPush({
        action: 'context', campaign_id: campaignId, creature_id: creatureId,
        ...(options.destination ? { destination_encounter_id: options.destination } : {}),
    });
    if (!context.ok) {
        container.innerHTML = `<p class="p-2 text-danger">${escapeHtml(pushErrorText(context))}</p>`;
        return;
    }

    const sourceId = String(context.sourceEncounterId ?? '');
    const destinationId = String(context.destinationEncounterId ?? sourceId);
    const encounters = [...(context.encounters ?? [])].sort((a, b) => Number(b.id === sourceId) - Number(a.id === sourceId));
    const rooms = context.rooms ?? [];
    const roster = [...(context.roster ?? [])]
        .sort((a, b) => Number(b.id === creatureId) - Number(a.id === creatureId) || a.name.localeCompare(b.name));
    const names = new Map(roster.map(entry => [entry.id, entry.name]));
    const rosterText = (entry: PushRosterEntry): string => {
        const tags = [entry.pc ? 'PC' : '', entry.dead ? 'dead' : '',
            entry.summonOf ? `summon of ${names.get(entry.summonOf) ?? 'another creature'}` : ''].filter(Boolean);
        return tags.length ? `${entry.name} (${tags.join(', ')})` : entry.name;
    };
    const field = (label: string, id: string, control: string): string =>
        `<div class="mb-3"><label class="form-label small text-uppercase mb-1" for="${id}">${escapeHtml(label)}</label>${control}</div>`;

    container.innerHTML = `
    <div class="p-2" data-ft-push-ec-form>
      <h5 class="d-flex align-items-center gap-2 mb-2">${iconMap('go_to_encounter', 25, 25, false)}<span>Push to Encounter</span></h5>
      <div class="small mb-2" role="status" data-ft-push-ec-status></div>
      ${field('Encounter', 'ft_push_ec_encounter', `<select id="ft_push_ec_encounter" class="ts-select ts-control-full-width" data-show-icons="false" data-ft-push-ec-encounter>
        ${encounters.map(encounter => `<option value="${escapeHtml(encounter.id)}"${encounter.id === destinationId ? ' selected' : ''}>${escapeHtml(encounterLabel(encounter, sourceId))}</option>`).join('')}
      </select>`)}
      ${field('Room', 'ft_push_ec_room', `<select id="ft_push_ec_room" class="ts-select ts-control-full-width" data-show-icons="false" data-ft-push-ec-room>
        ${rooms.map((room, index) => `<option value="${escapeHtml(room.id)}"${index === 0 ? ' selected' : ''}>${escapeHtml(roomLabel(room))}</option>`).join('')}
      </select>`)}
      ${field('Who goes', 'ft_push_ec_who', `<select id="ft_push_ec_who" class="ts-select ts-control-full-width" multiple data-ft-push-ec-who>
        ${roster.map(entry => `<option value="${escapeHtml(entry.id)}" data-icon="${escapeHtml(rosterIconHtml(entry))}"${entry.id === creatureId ? ' selected' : ''}>${escapeHtml(rosterText(entry))}</option>`).join('')}
      </select>`)}
      <p class="small opacity-75">Living summons follow their controller. Mounts, riders and pilots move together.</p>
      <div class="d-flex justify-content-end"><button type="button" class="btn btn-primary btn-sm" data-ft-push-ec-confirm>Push</button></div>
    </div>`;
    host.initializeTomSelect?.();

    const form = container.querySelector<HTMLElement>('[data-ft-push-ec-form]');
    const status = form?.querySelector<HTMLElement>('[data-ft-push-ec-status]');
    const encounterSelect = form?.querySelector<TsSelect>('[data-ft-push-ec-encounter]');
    const roomSelect = form?.querySelector<TsSelect>('[data-ft-push-ec-room]');
    const whoSelect = form?.querySelector<TsSelect>('[data-ft-push-ec-who]');
    const push = form?.querySelector<HTMLButtonElement>('[data-ft-push-ec-confirm]');
    if (!form || !status || !encounterSelect || !roomSelect || !whoSelect || !push) return;

    let busy = false;
    const selectedIds = (): string[] => Array.from(whoSelect.selectedOptions, option => option.value).filter(Boolean);
    const sync = (): void => { push.disabled = busy || !roomSelect.value || selectedIds().length === 0; };
    const setStatus = (text: string, loading = false): void => {
        status.innerHTML = loading ? `${pushSpinner()}<div class="text-center">${escapeHtml(text)}</div>` : escapeHtml(text);
    };
    setStatus(options.notice ?? (rooms.length ? PUSH_READY : PUSH_NO_ROOMS));
    if (options.notice) form.dataset.ftPushEcResult = 'ok';
    whoSelect.addEventListener('change', sync);
    roomSelect.addEventListener('change', sync);
    sync();

    encounterSelect.addEventListener('change', async () => {
        const destination = encounterSelect.value;
        if (!destination) return;
        busy = true;
        sync();
        roomSelect.tomselect?.disable();
        setStatus('Loading rooms…', true);
        const next = await postPush({ action: 'context', campaign_id: campaignId, creature_id: creatureId, destination_encounter_id: destination });
        if (encounterSelect.value !== destination || !form.isConnected) return; // a newer choice won
        const nextRooms = next.ok ? next.rooms ?? [] : [];
        const tom = roomSelect.tomselect;
        if (tom) {
            tom.clear(true);
            tom.clearOptions();
            nextRooms.forEach(room => tom.addOption({ value: room.id, text: roomLabel(room) }));
            tom.refreshOptions(false);
            if (nextRooms[0]) tom.setValue(nextRooms[0].id, true);
            tom.enable();
        } else {
            roomSelect.innerHTML = nextRooms.map(room => `<option value="${escapeHtml(room.id)}">${escapeHtml(roomLabel(room))}</option>`).join('');
        }
        busy = false;
        setStatus(next.ok ? (nextRooms.length ? PUSH_READY : PUSH_NO_ROOMS) : pushErrorText(next));
        sync();
    });

    push.addEventListener('click', async () => {
        const ids = selectedIds();
        if (busy || ids.length === 0 || !roomSelect.value) return;
        const destination = encounterSelect.value;
        const roomName = roomSelect.selectedOptions[0]?.textContent ?? 'the room';
        busy = true;
        sync();
        setStatus(`Pushing ${ids.length === 1 ? names.get(ids[0] ?? '') ?? 'creature' : `${ids.length} creatures`}…`, true);
        const result = await postPush({
            action: 'send', campaign_id: campaignId, destination_encounter_id: destination,
            room_id: roomSelect.value, creature_ids: ids, confirmed: true,
        });
        busy = false;
        if (!result.ok) { setStatus(pushErrorText(result)); sync(); return; }
        // Re-open from fresh truth: pushed creatures may have left this roster.
        await ftOpenPushEncounterCreatureForm(creatureId, {
            destination, notice: `Pushed ${result.movedCount ?? ids.length} to ${roomName}.`,
        });
    });
}
